import React from 'react';
import bemBlock from '@gik/core/utils/bemBlock';
import { FormProps } from '@gik/ui';
import { FormRenderProps } from 'react-final-form';
import noop from '@gik/core/utils/noop';
import { CalendarAnnouncementForm, ICalendarAnnouncementFormValues } from './CalendarAnnouncementForm';
import { calendarAnnouncementFormValuesToPayload } from '../EventForm/utils';
import { ICalendarAddRequestValues } from '../EventCreateForm/CalendarEventCreateFlow';
import { createAnnouncement, ICreateAnnouncementPayload } from '@gik/api/calendar/calendarAnnouncements';
import { useInkindStore } from '@gik/inkind/store/InkindStore';
import { useCalendarEventTypes } from '@gik/api/calendar/calendarEventType';
import { calendarEventMutatedAnalytics } from '@gik/calendar/utils/CalendarAnalytics';
import { useCalendarStore } from '@gik/calendar/store/CalendarStore';

interface ICalendarAddAnnouncementFormProps extends FormProps {
  initialValues: Partial<ICalendarAnnouncementFormValues>;
  buttons: (form: FormRenderProps, formId: string) => React.ReactNode;
  buttonsPortal?: () => HTMLElement;
  onSuccess?: (newEventId: string) => void;
  formValues: Partial<ICalendarAddRequestValues>;
  updateFormValues(values: Partial<ICalendarAddRequestValues>): void;
}

export function CalendarAddAnnouncementForm({
  className,
  buttonsPortal,
  initialValues,
  formValues,
  updateFormValues,
  onSuccess = noop,
  buttons,
  ...otherProps
}: ICalendarAddAnnouncementFormProps): React.ReactElement {
  const bem = bemBlock('calendar-add-announcement-form');
  const [errorMessage, setErrorMessage] = React.useState<string>();
  const inkindRouteId = useInkindStore(state => state.inkindRouteId);
  const { data: calendarEventTypes } = useCalendarEventTypes();

  const setFormSaving = useCalendarStore(state => state.setFormSaving);

  async function handleSubmit(values: ICalendarAnnouncementFormValues) {
    const newValues = { ...formValues, ...values } as ICalendarAddRequestValues;
    updateFormValues(newValues);

    const payload: ICreateAnnouncementPayload = {
      ...calendarAnnouncementFormValuesToPayload(values),
      typeId: formValues.eventTypeId,
      inkindRouteId,
    };

    setErrorMessage(null);
    setFormSaving(true);

    let newEventId: string;
    try {
      newEventId = await createAnnouncement(payload);
    } catch (err) {
      console.error(err);
      setErrorMessage(err.message);
      setFormSaving(false);
      return null;
    }

    const typeName = calendarEventTypes?.find(type => type.id === payload.typeId)?.name;

    if (newEventId) {
      calendarEventMutatedAnalytics(
        'eventCreated',
        newEventId,
        payload.allDay,
        payload.startsAt,
        payload.endsAt,
        payload.repeat,
        payload.description,
        payload.title,
        payload.typeId,
        typeName,
        undefined,
        {
          announcementTypeId: payload.announcementTypeId,
          sendEmailToPageFollowers: payload.sendEmailToPageFollowers,
        }
      );

      onSuccess(newEventId);
    }
  }

  return (
    <div className={bem(null, null, className)}>
      <CalendarAnnouncementForm
        initialValues={{ ...initialValues }}
        buttonsPortal={buttonsPortal}
        errorMessage={errorMessage}
        buttons={buttons}
        {...otherProps}
        onSubmit={handleSubmit}
      />
    </div>
  );
}
